import { store } from 'store/store';
import { formatAmount, formatNumber, parseDanishAmount } from '../utilities/format';

const doubleCurlyRegex = /{{(.+?)}}/g;

const formatDanishAmount = (num: number): string => (Number.isInteger(num) ? formatNumber(num) : formatAmount(num));

// Presentation-only formatter registry for `{{NAME | formatter}}` placeholders. Values in the
// store are kept verbatim (see calculatorFormSlice); formatters normalize on read.
const FORMATTERS: Record<string, (raw: string) => string> = {
	currency: (raw) => {
		const num = parseDanishAmount(raw);
		return Number.isFinite(num) ? `${formatDanishAmount(num)} kr.` : raw;
	},
};

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

// Split "{{NAME | formatter}}" (with or without whitespace) into the lookup name and an
// optional formatter id. Formatter is undefined when the placeholder has no pipe.
const parsePlaceholder = (match: string): { name: string; formatter?: string } => {
	const inner = match.replace(/^\{\{|\}\}$/g, '');
	const pipeIdx = inner.indexOf('|');
	if (pipeIdx === -1) return { name: inner.trim() };
	const name = inner.slice(0, pipeIdx).trim();
	const formatter = inner.slice(pipeIdx + 1).trim();
	return { name, formatter: formatter || undefined };
};

const applyFormatter = (raw: unknown, formatter: string | undefined): string | undefined => {
	if (raw === undefined || raw === null || raw === '') return undefined;
	if (typeof raw === 'object' || typeof raw === 'function') return undefined;
	const asString = raw.toString();
	if (!formatter) return asString;
	const fn = FORMATTERS[formatter.toLowerCase()];
	return fn ? fn(asString) : asString;
};

// Exposed so components displaying raw store values (e.g. InputTextFieldComponent with a
// `valueFormat` prop driven by the CMS `InputTextfieldBlock.Format` field) can render them
// with the same locale formatting as `{{X | currency}}` placeholders.
export { applyFormatter as formatValue };
export type PlaceholderFormatter = 'currency';

const enum FIELD_PROPERTY {
	TITLE = 'title',
	SUBTITLE = 'subtitle',
	LABEL = 'label',
	CONTENT = 'content',
	TEXT = 'text',
	REGULAR_PRICE_LABEL = 'regularPriceLabel',
	YOUR_PRICE_LABEL = 'yourPriceLabel',
	ADDITIONAL_PRICE_DETAILS = 'additionalPriceDetails',
	TOTAL_PRICE = 'totalPrice',
}

export const replacePlaceholders = (field: FieldsBlock) => {
	switch (field?.contentType) {
		case 'CalculatorStepBlock':
		case 'RadioCardFieldBlock':
			replacePlaceholderByType(field, FIELD_PROPERTY.TITLE);
			break;
		case 'CheckboxFieldBlock':
			replacePlaceholderByType(field, FIELD_PROPERTY.LABEL);
			break;
		case 'TeaserCardBlock':
		case 'UserConsentBlock':
			replacePlaceholderByType(field, FIELD_PROPERTY.CONTENT);
			break;
		case 'ParagraphBlock':
			replacePlaceholderByType(field, FIELD_PROPERTY.TEXT);
			break;
		default:
			return;
	}
};

export const replacePlaceholdersModal = (field: FieldsBlock) => {
	replacePlaceholderByType(field, FIELD_PROPERTY.CONTENT);
	replacePlaceholderByType(field, FIELD_PROPERTY.TITLE);
	replacePlaceholderByType(field, FIELD_PROPERTY.SUBTITLE);
};

export const replacePlaceholdersSummaryComponent = (field: SummaryProductDetailsBlock) => {
	replacePlaceholderByType(field, FIELD_PROPERTY.REGULAR_PRICE_LABEL);
	replacePlaceholderByType(field, FIELD_PROPERTY.YOUR_PRICE_LABEL);
	replacePlaceholderByType(field, FIELD_PROPERTY.ADDITIONAL_PRICE_DETAILS);
};

export const replacePlaceholdersSummaryTotalPriceComponent = (field: SummaryTotalPriceDetailsBlock) => {
	replacePlaceholderByType(field, FIELD_PROPERTY.TOTAL_PRICE);
};

export const getReplacedValue = (value: string) => {
	const matches = value?.match(doubleCurlyRegex);
	if (matches?.length > 0) {
		return replacePlaceholder(value, matches);
	}
	return value;
};

const replacePlaceholderByType = (field: FieldsBlock | SummaryProductDetailsBlock, propertyType: FIELD_PROPERTY) => {
	if (field?.[propertyType]) {
		if (Array.isArray(field[propertyType])) {
			field[propertyType] = field[propertyType].map((item) => {
				const matches = item.match(doubleCurlyRegex);
				return matches?.length > 0 ? replacePlaceholder(item, matches) : item;
			});
		} else {
			const matches = field[propertyType].match(doubleCurlyRegex);
			if (matches?.length > 0) {
				field[propertyType] = replacePlaceholder(field[propertyType], matches);
			}
		}
	}
};

const replacePlaceholder = (value: string, matches: string[]) => {
	let result = value;
	let regMatch: RegExp;
	let replacement: string;
	const definitions = store.getState().placeholderDefinitions.definitions;
	const form = store.getState().calculatorForm;

	matches.forEach((match) => {
		regMatch = new RegExp(escapeRegExp(match), 'gi');
		replacement = getFormValue(match, form);
		if (replacement === '') {
			// check if value is present in definitions
			replacement = getPlaceholderReplacement(match, definitions, form);
		}
		if (!isDevelop() || !result.includes('!NA{{')) {
			result = result.replace(regMatch, replacement).trim();
		}
	});
	return result;
};

const getFormValue = (placeholder: string, form) => {
	const { name, formatter } = parsePlaceholder(placeholder);
	const entry = form[name.toUpperCase()]?.data;
	if (!entry) return '';
	if (formatter) {
		return applyFormatter(entry.value, formatter) ?? '';
	}
	return entry.label ?? entry.value ?? '';
};

const getPlaceholderReplacement = (placeholder: string, definitions, form) => {
	const { name, formatter } = parsePlaceholder(placeholder);
	const definitionMatch = definitions[name.toUpperCase()];
	const entry = definitionMatch ? form[definitionMatch]?.data : undefined;
	const value = formatter ? applyFormatter(entry?.value, formatter) : (entry?.label ?? entry?.value);
	if (value) {
		return value;
	} else if (isDevelop()) {
		// for debug in test environment
		return `!NA${placeholder}`;
	}
	// remove unknown placeholder from text
	return '';
};

const isDevelop = () => {
	return location.href.startsWith('https://localhost') || location.href.startsWith('https://sit-');
};
